import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';

const ProductSearch = ({ onSearch, onFilterToggle, products = [], className = "" }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const searchRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef?.current && !searchRef?.current?.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchChange = (e) => {
    const query = e?.target?.value;
    setSearchQuery(query);
    
    // Call onSearch immediately for real-time filtering
    if (onSearch) {
      onSearch(query);
    }
    
    if (query?.length > 1) {
      const filtered = products
        ?.filter(product =>
          product?.name?.toLowerCase()?.includes(query?.toLowerCase()) ||
          product?.category?.toLowerCase()?.includes(query?.toLowerCase())
        )
        ?.map(p => ({
          id: p.id,
          text: p.name,
          category: p.category,
          type: 'product'
        }))
        ?.slice(0, 8);
        
      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e?.preventDefault();
    if (searchQuery?.trim()) {
      onSearch(searchQuery?.trim());
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion?.text);
    onSearch(suggestion?.text);
    setShowSuggestions(false);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    onSearch('');
    setShowSuggestions(false);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'product': return 'Package';
      case 'brand': return 'Building';
      case 'category': return 'Layers';
      case 'filter': return 'Filter';
      case 'specialty': return 'Stethoscope';
      default: return 'Search';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'product': return 'text-primary';
      case 'brand': return 'text-success';
      case 'category': return 'text-warning';
      case 'filter': return 'text-accent';
      case 'specialty': return 'text-secondary';
      default: return 'text-text-secondary';
    }
  };

  return (
    <div className={`relative ${className}`} ref={searchRef}>
      {/* Search Form */}
      <form onSubmit={handleSearchSubmit} className="relative">
        <div className="relative">
          <Input
            type="text"
            placeholder="Search products..."
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={() => searchQuery?.length > 0 && setShowSuggestions(true)}
            className="pl-14 pr-16 h-14 text-lg border-2 border-border/60 focus:border-primary transition-all duration-300 shadow-sm rounded-2xl"
          />
          
          {/* Search Icon */}
          <div className="absolute left-5 top-1/2 transform -translate-y-1/2">
            <Icon name="Search" size={22} className="text-text-secondary" />
          </div>

          {/* Mobile Filter Toggle Inside Input */}
          <button
            type="button"
            onClick={onFilterToggle}
            className="lg:hidden absolute right-3 top-1/2 transform -translate-y-1/2 p-2 bg-primary/10 hover:bg-primary/20 rounded-xl transition-colors border border-primary/20"
            aria-label="Toggle filters"
          >
            <Icon name="SlidersHorizontal" size={20} className="text-primary" />
          </button>
          
          {/* Clear Button */}
          {searchQuery && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="absolute right-14 lg:right-5 top-1/2 transform -translate-y-1/2 p-1.5 hover:bg-muted rounded-full transition-colors duration-200"
              aria-label="Clear search"
            >
              <Icon name="X" size={18} className="text-text-secondary" />
            </button>
          )}
        </div>
      </form>
      {/* Search Suggestions */}
      <AnimatePresence>
        {showSuggestions && suggestions?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-lg shadow-medical-hover z-50 overflow-hidden"
          >
            <div className="py-2">
              <div className="px-4 py-2 text-xs font-medium text-text-secondary uppercase tracking-wide border-b border-border">
                Suggestions
              </div>
              
              {suggestions?.map((suggestion) => (
                <button
                  key={suggestion?.id}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="w-full px-4 py-3 text-left hover:bg-muted transition-colors duration-200 flex items-center space-x-3"
                >
                  <div className={`flex-shrink-0 ${getTypeColor(suggestion?.type)}`}>
                    <Icon name={getTypeIcon(suggestion?.type)} size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-text-primary truncate">
                      {suggestion?.text}
                    </div>
                    <div className="text-xs text-text-secondary">
                      in {suggestion?.category}
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <Icon name="ArrowUpRight" size={14} className="text-text-secondary" />
                  </div>
                </button>
              ))}
              
              {/* Search All Results */}
              <div className="border-t border-border mt-2">
                <button
                  onClick={handleSearchSubmit}
                  className="w-full px-4 py-3 text-left hover:bg-muted transition-colors duration-200 flex items-center space-x-3"
                >
                  <div className="flex-shrink-0 text-primary">
                    <Icon name="Search" size={16} />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-primary">
                      Search for "{searchQuery}"
                    </div>
                    <div className="text-xs text-text-secondary">
                      View all results
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showSuggestions && searchQuery?.length === 0 && products?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-lg shadow-medical-hover z-50 overflow-hidden"
          >
            <div className="py-2">
              <div className="px-4 py-2 text-xs font-medium text-text-secondary uppercase tracking-wide border-b border-border">
                Featured Products
              </div>
              
              {products?.slice(0, 4)?.map((product) => (
                <button
                  key={product?.id}
                  onClick={() => handleSuggestionClick({ text: product.name })}
                  className="w-full px-4 py-3 text-left hover:bg-muted transition-colors duration-200 flex items-center space-x-3"
                >
                  <div className="flex-shrink-0 text-primary">
                    <Icon name="Package" size={16} />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-text-primary">
                      {product?.name}
                    </div>
                    <div className="text-xs text-text-secondary">
                      {product?.category}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductSearch;